import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import ConsoleStore from '../../../stores/ConsoleStore';
import { USER_GROUPS_SOURCES } from '../../../stores/Policy/BrowsingRoles/BrowsingRolesStore';
import StatefulBulkEditor, { BULK_EDITOR_STATE } from '../../BulkEditor/StatefulBulkEditor';
import FuseConsoleCardFooter from '../../ConsoleFragments/FuseConsoleCardFooter';
import Card from '../../Fuse/Card';
import FuseTabs from '../../Fuse/FuseTabs';
import { FTHeading, FTInput } from '../../Fuse';
import FuseAlert from '../../FuseControls/FuseAlert';
import FuseReadOnlySelect from '../../FuseControls/FuseReadOnlySelect';
import UserGroupsStore, { USER_GROUP_TABS } from '../../../stores/Policy/BrowsingRoles/UserGroupsStore';
import { ModalMode } from '../../../stores/Policy/PolicyModalStore';
import MatchAllPill from '../../MatchAllPill/MatchAllPill';
import { ConsoleBody } from '../../Text/Text';
import './UserGroups.css';

const block = b.lock('UserGroups');

class UserGroups extends Component {
    static propTypes = {
        store: UserGroupsStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
        mode: PropTypes.oneOf(Object.values(ModalMode)).isRequired,
    };

    componentDidMount() {
        this.props.store.loadGroups();
        this.props.store.loadLastSyncGroup();
        this.props.store.setCustomerDomainsGetter(() => this.props.console.customerDomains);
    }

    userGroups() {
        const { store, mode } = this.props;
        const readOnly = mode === ModalMode.VIEW || mode === ModalMode.DELETE;

        return <FuseReadOnlySelect
            containerClassName={block('UsersSelect', { readOnly })}
            readOnly={readOnly}
            store={store.effectsUserGroups}
            selectAllEnabled
            fullWidth
            searchable
            searchLabel="Search by Group Name or Email"
            noOptionsLabel="No entries found"
            placeholder={!readOnly ? 'Select User Groups' : ''}
            loading={store.loadStatus.isLoading}
            onFilter={store.effectsUserGroups.searchHandle}
        />;
    }

    bulkOptions = () => {
        const { store, mode } = this.props;

        switch (mode) {
        case ModalMode.DELETE:
        case ModalMode.VIEW: return {
            type: BULK_EDITOR_STATE.READ_ONLY,
            store
        };
        case ModalMode.EDIT_CONFIRM: return {
            type: BULK_EDITOR_STATE.EDIT_CONFIRM,
            oldItems: store.originalValue.effectsEmails,
            newItems: store.getValue().effectsEmails || [],
            removedItemsSearch: store.editConfirmRemovedSearch,
            addedItemsSearch: store.editConfirmAddedSearch,
        };
        default: return {
            type: BULK_EDITOR_STATE.NORMAL,
            store
        };
        }
    }

    getErrorMessage = () => {
        const { store } = this.props;
        const { effectsEmails } = store;
        const failedValidators =
            effectsEmails.urlBulkEditorStore.bulkEditorStore.failedValidatorsIndexes;

        // e.g. init
        if (failedValidators === undefined || failedValidators.length === 0) {
            return;
        }
        const msg = failedValidators.map((v) =>
            (store.isCustomerDomainValidator(v)
                ? 'Email domain is not associated with your account'
                : 'Invalid email')
        ).join('\n');

        return msg;
    };

    emails() {
        const { store, mode } = this.props;
        const opts = this.bulkOptions();

        return <div className={block('EmailsWrapper', { mode })}>
            <StatefulBulkEditor
                entityName={'Email Addresses'}
                wrapperClassName={block('BulkEditorNoCardWrapper')}
                action={null}
                title={''}
                subTitle={'Enter or paste email addresses (1 entry per line)'}
                placeholder={'Enter or paste email addresses'}
                validUrlText={'Entries'}
                errorMassage={this.getErrorMessage()}
                warningMassage={'Duplicate emails will be deleted on save'}
                mode={opts}
                store={store.effectsEmails}
                readOnlyHeader={<FTHeading>Assigned User Emails</FTHeading>}
                searchPlaceholder="Search by Email Address"
            />
        </div>;
    }

    renderFooter = (isDefaultViewOnly) => {
        const { store, console } = this.props;

        return <FuseConsoleCardFooter
            className={isDefaultViewOnly ? '' : block('Footer')}
            text={<Fragment>
                {store.isDefault
                    ? 'The Default Browsing Role is assigned to all users.'
                    : `At least one User Group or User Email from ${console.customerName} is required to be selected.`}
            </Fragment>}
        />;
    };

    render() {
        const { store, mode } = this.props;

        const DEFAULT_ITEM = 'defaultItem';
        const userGroupModes = store.isDefault ? DEFAULT_ITEM : mode;
        const lastSyncGroupText = store.lastSyncGroupDate ? `Last Synchronization: ${store.lastSyncGroupDate}` : '';

        switch (userGroupModes) {
        case DEFAULT_ITEM:
        case ModalMode.DELETE:
        case ModalMode.VIEW: return <Fragment>
            {store.isDefault ? <Fragment>
                <Card title={'Assigned Users'}>
                    <div className={block('AllUsers')}>
                        <MatchAllPill/>
                    </div>
                    {this.renderFooter(true)}
                </Card>
            </Fragment> : null}
            {!store.isDefault ? <Fragment>
                {store.effectsUserGroups.selectedValues.length ? <Card title={<Fragment>
                    Assigned User Groups
                    {lastSyncGroupText ? <ConsoleBody>&nbsp;- {lastSyncGroupText}</ConsoleBody> : null}
                </Fragment>}
                >
                    {this.userGroups()}
                </Card> : null}
                {!store.effectsEmails.isEmpty() ? this.emails() : null}
            </Fragment> : null}
        </Fragment>;

        case ModalMode.EDIT_CONFIRM: return this.emails();

        default: return <Card title="Assigned Users">
            <FuseTabs
                className={block('UsersTab')}
                containerClassName={block('Users')}
                store={store.activeTab}
                tabs={[
                    { label: 'User Groups', value: USER_GROUP_TABS.GROUPS, qa: 'tab-groups' },
                    { label: 'User Emails', value: USER_GROUP_TABS.EMAILS, qa: 'tab-emails' },
                ]}
            />
            {store.activeTab.getValue() === USER_GROUP_TABS.GROUPS ? <Fragment>
                {store.source !== USER_GROUPS_SOURCES.NONE ? <Fragment>
                    {lastSyncGroupText ?
                        <ConsoleBody className={block('LastSyncGroup')}>{lastSyncGroupText}</ConsoleBody> : null}
                    {this.userGroups()}
                </Fragment> : null}
                {store.source === USER_GROUPS_SOURCES.NONE ? <FuseAlert style={{ marginTop: 20 }}>
                        Select a source for <b> Browsing Roles - User Group Source </b> to synchronize user groups.
                </FuseAlert> : null }
            </Fragment> : null}

            {store.activeTab.getValue() === USER_GROUP_TABS.EMAILS ? this.emails() : null }

            {store.referencesUnSyncedGroups() && !store.loadStatus.isLoading ?
                <FTInput className={block('UnSyncedMessage')}>
                    User Groups displayed in red are not available in the source system for synchronization.
                </FTInput>
                : null}

            {this.renderFooter()}
        </Card>;
        }
    }
}

export default inject('console')(observer(UserGroups));
